/**
 * Global toast renderer. Mount once at the app root; any code can fire
 * a toast via the `toast.success(...)` / `toast.error(...)` etc.
 * helpers in `lib/toast.ts` — no context wiring per call site.
 *
 * Design ported from the Kora admin template's ToastPage:
 *   /Users/nielsvanharen/development/mijnrechtzaak/template/
 *     Kora-Main-File/Template/src/pages/components/ToastPage.tsx
 *
 * Ported behaviour:
 *   - Stacked per position (top-left / top-right / bottom-left etc)
 *   - Variant icons + accent bar (success / error / warning / info / neutral)
 *   - Hover-to-pause auto-dismiss
 *   - Animated mount + unmount via opacity + translate
 *   - Bottom progress sliver scaled to remaining duration
 *   - Portal to document.body so dialog z-index can't bury them
 *
 * Adapted: swapped Kora's `bg-white dark:bg-gray-800` for our
 * `mk-card` glass + `text-text-primary` tokens to match the rest of
 * the Kora-dark Merkflow app (which uses `light-dark()` automatic
 * mode-flipping under the hood — the toasts inherit that for free).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import {
  registerToastHandler,
  type ToastInput,
  type ToastPosition,
  type ToastVariant,
} from '@/lib/toast';

interface ToastEntry extends ToastInput {
  id: string;
}

const ALL_POSITIONS: ToastPosition[] = [
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

export function Toaster() {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Register / deregister with the module-level singleton in lib/toast.ts.
  useEffect(() => {
    const add = (input: ToastInput): string => {
      const id = Math.random().toString(36).slice(2, 11);
      setToasts((prev) => [...prev, { ...input, id }]);
      return id;
    };
    registerToastHandler(add);
    return () => registerToastHandler(null);
  }, []);

  if (toasts.length === 0 || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[9999] overflow-hidden">
      {ALL_POSITIONS.map((pos) => {
        const here = toasts.filter((t) => (t.position ?? 'bottom-right') === pos);
        if (here.length === 0) return null;
        const isTop = pos.startsWith('top');
        const isCenter = pos.endsWith('-center');
        const isRight = pos.endsWith('-right');
        return (
          <div
            key={pos}
            className={`absolute flex max-w-full flex-col gap-3 p-4 md:p-6 ${
              isTop ? 'top-0' : 'bottom-0'
            } ${
              isCenter
                ? 'left-1/2 -translate-x-1/2 items-center'
                : isRight
                  ? 'right-0 items-end'
                  : 'left-0 items-start'
            }`}
          >
            {here.map((t) => (
              <ToastItem key={t.id} {...t} onClose={remove} />
            ))}
          </div>
        );
      })}
    </div>,
    document.body,
  );
}

function ToastItem({
  id,
  message,
  heading,
  variant = 'neutral',
  duration = 5000,
  action,
  onClose,
  onDismiss,
}: ToastEntry & { onClose: (id: string) => void }) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const close = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      onClose(id);
      onDismiss?.();
    }, 300);
  }, [id, onClose, onDismiss]);

  // Animate in
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Auto-dismiss timer
  useEffect(() => {
    if (duration > 0) {
      timerRef.current = setTimeout(close, duration);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [duration, close]);

  const icon = ICONS[variant];
  const accent = ACCENT[variant];

  return (
    <div
      role="alert"
      onMouseEnter={() => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }}
      onMouseLeave={() => {
        if (duration > 0 && !timerRef.current) {
          timerRef.current = setTimeout(close, duration);
        }
      }}
      className={`border-border bg-card pointer-events-auto relative w-full max-w-sm overflow-hidden rounded-xl border shadow-xl backdrop-blur-xl transition-all duration-300 ease-out ${
        visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-95 opacity-0'
      }`}
    >
      <div className="flex items-start gap-3 p-4">
        {icon && <div className="mt-0.5 flex-shrink-0">{icon}</div>}
        <div className="min-w-0 flex-1">
          {heading && <h5 className="text-text-primary mb-0.5 text-sm font-bold">{heading}</h5>}
          <p
            className={`text-text-secondary text-sm leading-relaxed ${!heading ? 'font-medium' : ''}`}
          >
            {message}
          </p>
          {action && <div className="mt-3">{action}</div>}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            close();
          }}
          className="text-text-tertiary hover:bg-subtle hover:text-text-primary flex-shrink-0 rounded p-0.5 transition-colors"
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {duration > 0 && (
        // Progress sliver at the bottom — width transitions from 100% → 0 over
        // the toast's duration, mirroring Kora's visual flare.
        <div
          className={`absolute bottom-0 left-0 h-0.5 ${accent} opacity-40`}
          style={{
            width: visible ? '0%' : '100%',
            transition: `width ${duration}ms linear`,
          }}
        />
      )}
    </div>
  );
}

const ICONS: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle2 className="h-5 w-5 text-emerald-400" />,
  error: <AlertCircle className="h-5 w-5 text-red-400" />,
  warning: <AlertTriangle className="h-5 w-5 text-amber-400" />,
  info: <Info className="h-5 w-5 text-blue-400" />,
  neutral: null,
};

const ACCENT: Record<ToastVariant, string> = {
  success: 'bg-emerald-500',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
  neutral: 'bg-text-tertiary',
};
